import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Calendar, MessageCircle, Sparkles, FileText, KeyRound, Clock, Share2, Copy, Check, ChevronUp, ChevronDown, HelpCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";

const CTA_DETAILS = {
  orientacion: {
    icon: Sparkles,
    title: "¿No sabes por dónde empezar?",
    desc: "Una videoconferencia de 30 min · 15 €. Te explicamos lo esencial.",
    btn: "Reservar Orientación · 15 €",
    href: "/orientacion",
  },
  consultoria: {
    icon: FileText,
    title: "¿Tienes una nota simple delante?",
    desc: "Informe profesional sobre cargas y riesgos · 95 €. Lo recibes por email.",
    btn: "Solicitar Consultoría · 95 €",
    href: "/consultoria",
  },
  informe_premium: {
    icon: KeyRound,
    title: "¿Vas en serio con esta subasta?",
    desc: "Acompañamiento completo: análisis profundo, asistencia durante la puja y resolución de dudas en directo.",
    btn: "Saber más · Informe Premium",
    href: "/informe-premium",
  },
};

// Average reading speed in Spanish ~200 words/min
const readingTime = (text) => Math.max(1, Math.round((text || "").split(/\s+/).length / 200));

// Split markdown into main content + FAQ section
function splitFaq(md) {
  const re = /\n##\s+Preguntas frecuentes\s*\n/i;
  const m = md.match(re);
  if (!m) return { main: md, faq: null };
  const idx = m.index;
  return { main: md.slice(0, idx).trim(), faq: md.slice(idx + m[0].length).trim() };
}

// Parse FAQ markdown into [{q,a}] pairs (### q / paragraph a)
function parseFaq(text) {
  if (!text) return [];
  const items = [];
  const re = /###\s+(.+?)\n([\s\S]*?)(?=\n###\s+|$)/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    items.push({ q: m[1].trim(), a: m[2].trim() });
  }
  return items;
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/blog/posts/${slug}`);
        if (!cancelled) setPost(data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (notFound) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-4xl font-black mb-4">Artículo no encontrado</h1>
          <button onClick={() => navigate("/blog")} className="text-[#E0651F] hover:underline">← Ver todos los artículos</button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!post) return <div className="min-h-screen bg-stone-950 text-stone-100"><SiteHeader /><div className="text-center py-24 text-stone-500">Cargando…</div></div>;

  const cta = CTA_DETAILS[post.cta_target] || CTA_DETAILS.orientacion;
  const Icon = cta.icon;
  const url = `https://fmsubastas.com/blog/${post.slug}`;
  const { main, faq } = splitFaq(post.content_markdown || "");
  const faqItems = parseFaq(faq);
  const minutes = readingTime(post.content_markdown);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* ignore */ }
  };
  const shareWhatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(post.title + " " + url)}`, "_blank", "noopener");
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener");

  // JSON-LD: Article + FAQPage
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "author": { "@type": "Person", "name": post.author || "Fernando Marcos" },
    "publisher": {
      "@type": "Organization",
      "name": "Fernando Marcos Subastas & Investment",
      "url": "https://fmsubastas.com",
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
    "keywords": (post.keywords || []).join(", "),
  };
  const faqJsonLd = faqItems.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((it) => ({
      "@type": "Question",
      "name": it.q,
      "acceptedAnswer": { "@type": "Answer", "text": it.a.replace(/[*_`#>]/g, "").slice(0, 700) },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <Helmet>
        <title>{`${post.title} | FM Subastas`}</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        {post.cover_image_url && (
          <meta property="og:image" content={post.cover_image_url.startsWith("http") ? post.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${post.cover_image_url}`} />
        )}
        {post.cover_image_url && <meta name="twitter:card" content="summary_large_image" />}
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>
      <SiteHeader />

      <article className="max-w-3xl mx-auto px-6 pt-12 pb-20">
        <Link to="/blog" data-testid="post-back-to-blog" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-stone-400 hover:text-white mb-10">
          <ArrowLeft className="w-4 h-4" /> Todos los artículos
        </Link>

        {/* Header */}
        <header className="mb-12">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-4">{post.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-black tracking-tight text-white leading-[1.08] mb-6">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
            <span className="text-stone-700">·</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {minutes} min de lectura</span>
            <span className="text-stone-700">·</span>
            <span>Por {post.author || "Fernando Marcos"}</span>
          </div>
        </header>

        {/* Cover image (hero) o emoji fallback */}
        {post.cover_image_url ? (
          <div className="mb-12 -mx-6 sm:mx-0 sm:rounded-sm overflow-hidden">
            <img
              src={post.cover_image_url.startsWith("http") ? post.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${post.cover_image_url}`}
              alt={post.cover_image_alt || post.title}
              className="w-full aspect-[16/9] object-cover"
              data-testid="blog-post-hero-image"
            />
          </div>
        ) : (
          <div className="text-7xl sm:text-8xl mb-12 text-center" aria-hidden="true">{post.cover_emoji || "📄"}</div>
        )}

        {/* Share bar */}
        <div className="flex items-center gap-2 mb-10 pb-6 border-b border-stone-800">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mr-2">Compartir:</span>
          <button onClick={shareWhatsapp} title="Compartir por WhatsApp"
            data-testid="share-whatsapp"
            className="w-8 h-8 inline-flex items-center justify-center bg-stone-800 hover:bg-emerald-600 text-stone-300 hover:text-white rounded-sm transition-colors">
            <MessageCircle className="w-4 h-4" />
          </button>
          <button onClick={shareFacebook} title="Compartir en Facebook"
            data-testid="share-facebook"
            className="w-8 h-8 inline-flex items-center justify-center bg-stone-800 hover:bg-blue-600 text-stone-300 hover:text-white rounded-sm transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
          <button onClick={copyLink} title="Copiar enlace"
            data-testid="share-copy"
            className="w-8 h-8 inline-flex items-center justify-center bg-stone-800 hover:bg-[#E0651F] text-stone-300 hover:text-white rounded-sm transition-colors">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
          {copied && <span className="text-[11px] text-emerald-400 ml-1">¡Enlace copiado!</span>}
        </div>

        {/* Article body */}
        <div className="post-content max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            blockquote: ({ children }) => (
              <blockquote className="not-prose my-7 pl-5 pr-4 py-4 border-l-4 border-[#E0651F] bg-[#E0651F]/5 rounded-r-sm text-stone-200 text-[15px] leading-relaxed">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E0651F] mb-1.5">Normativa / nota</div>
                {children}
              </blockquote>
            ),
          }}>{main}</ReactMarkdown>
        </div>

        {/* FAQ section */}
        {faqItems.length > 0 && (
          <section className="mt-14 pt-10 border-t border-stone-800">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="w-5 h-5 text-[#E0651F]" />
              <h2 className="font-display text-2xl font-black text-white tracking-tight">Preguntas frecuentes</h2>
            </div>
            <div className="space-y-3">
              {faqItems.map((it, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} className={`border rounded-sm transition-colors ${open ? "border-[#E0651F]/60 bg-stone-900" : "border-stone-800 bg-stone-900/50"}`}>
                    <button onClick={() => setOpenFaq(open ? null : i)}
                      data-testid={`faq-item-${i}`}
                      className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-stone-800/30 transition-colors">
                      <span className={`font-display font-bold text-base flex-1 ${open ? "text-[#ff7a30]" : "text-white"}`}>{it.q}</span>
                      {open ? <ChevronUp className="w-4 h-4 text-stone-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-stone-500 flex-shrink-0" />}
                    </button>
                    {open && (
                      <div className="px-5 pb-5 pt-1 text-stone-300 text-[15px] leading-relaxed faq-answer">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.a}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* CTA Block */}
        <div className="mt-16 bg-gradient-to-br from-[#E0651F]/15 to-transparent border border-[#E0651F]/40 rounded-sm p-8" data-testid="post-cta">
          <Icon className="w-8 h-8 text-[#E0651F] mb-3" />
          <h3 className="font-display text-2xl font-black text-white mb-2">{cta.title}</h3>
          <p className="text-stone-300 mb-5">{cta.desc}</p>
          <div className="flex flex-wrap gap-3">
            <Link to={cta.href} className="inline-flex items-center gap-2 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold rounded-sm">
              {cta.btn} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
               onClick={(e) => { e.preventDefault(); openWhatsApp(WA_MSG.generic); }}
               className="inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-700 border border-stone-700 text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold rounded-sm">
              <MessageCircle className="w-4 h-4" /> Consultar por WhatsApp
            </a>
          </div>
        </div>

        {/* Related */}
        {post.related && post.related.length > 0 && (
          <div className="mt-16 pt-12 border-t border-stone-800">
            <h3 className="font-display text-xl font-black text-white mb-6">Sigue leyendo</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {post.related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="bg-stone-900 border border-stone-800 hover:border-[#E0651F]/40 p-5 rounded-sm transition-colors">
                  {r.cover_image_url ? (
                    <img src={r.cover_image_url.startsWith("http") ? r.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${r.cover_image_url}`} alt={r.cover_image_alt || r.title} className="w-full aspect-[16/9] object-cover rounded-sm mb-3" loading="lazy" />
                  ) : (
                    <div className="text-3xl mb-2">{r.cover_emoji || "📄"}</div>
                  )}
                  <div className="font-display font-bold text-white mb-1 line-clamp-2">{r.title}</div>
                  <div className="text-xs text-stone-500 line-clamp-2">{r.excerpt}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Back-to-top floating button */}
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="back-to-top"
          aria-label="Volver arriba"
          className="fixed bottom-6 right-6 w-11 h-11 inline-flex items-center justify-center bg-[#E0651F] hover:bg-[#ff7a30] text-white rounded-full shadow-lg shadow-black/40 z-40 transition-colors">
          <ChevronUp className="w-5 h-5" />
        </button>
      )}

      <SiteFooter />

      <style>{`
        .post-content > p:first-of-type::first-letter {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 4em;
          font-weight: 900;
          float: left;
          line-height: 0.85;
          padding: 0.05em 0.14em 0 0;
          color: #E0651F;
        }
        .post-content p {
          color: #d6d3d1;
          font-size: 17px;
          line-height: 1.78;
          margin: 0 0 1.5em 0;
        }
        .post-content h2 {
          font-family: 'Playfair Display', Georgia, serif;
          color: #fff;
          font-weight: 900;
          font-size: 30px;
          letter-spacing: -0.02em;
          margin: 3em 0 1em;
          padding: 0.4em 0 0.4em 22px;
          border-left: 5px solid #E0651F;
          line-height: 1.18;
          background: linear-gradient(90deg, rgba(224,101,31,0.08) 0%, transparent 60%);
        }
        .post-content h2::before {
          content: "";
        }
        .post-content h3 {
          font-family: 'Playfair Display', Georgia, serif;
          color: #ff9655;
          font-weight: 700;
          font-size: 22px;
          margin: 2.2em 0 0.8em;
          line-height: 1.25;
          letter-spacing: -0.01em;
        }
        .post-content h3::before {
          content: "▸ ";
          color: #E0651F;
          font-weight: 900;
        }
        .post-content ul, .post-content ol {
          margin: 0 0 1.7em 0;
          padding: 0;
          list-style: none;
        }
        .post-content ul li, .post-content ol li {
          position: relative;
          padding: 0.4em 0 0.4em 2em;
          margin-bottom: 0.4em;
          color: #d6d3d1;
          line-height: 1.65;
          border-bottom: 1px solid rgba(120,113,108,0.15);
        }
        .post-content ul li:last-child, .post-content ol li:last-child {
          border-bottom: none;
        }
        .post-content ul li::before {
          content: "";
          position: absolute;
          left: 0.5em;
          top: 1em;
          width: 9px;
          height: 9px;
          background: #E0651F;
          transform: rotate(45deg);
        }
        .post-content ol {
          counter-reset: ol-counter;
        }
        .post-content ol li {
          counter-increment: ol-counter;
        }
        .post-content ol li::before {
          content: counter(ol-counter, decimal-leading-zero);
          position: absolute;
          left: 0;
          top: 0.55em;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.85em;
          font-weight: 700;
          color: #E0651F;
        }
        .post-content strong {
          color: #ff9655;
          font-weight: 700;
          background: linear-gradient(180deg, transparent 65%, rgba(224,101,31,0.22) 65%);
          padding: 0 2px;
        }
        .post-content a {
          color: #E0651F;
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-thickness: 1px;
        }
        .post-content a:hover { color: #ff9655; }
        .post-content code {
          background: #292524;
          color: #fbbf24;
          padding: 2px 6px;
          font-size: 0.85em;
          border-radius: 3px;
        }
        .post-content table {
          width: 100%;
          margin: 2em 0;
          border-collapse: collapse;
          font-size: 14px;
          background: #1c1917;
        }
        .post-content table th {
          background: #0c0a09;
          color: #E0651F;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 10px;
          letter-spacing: 0.14em;
          padding: 12px;
          text-align: left;
          border-bottom: 2px solid #E0651F;
        }
        .post-content table td {
          padding: 10px 12px;
          color: #d6d3d1;
          border-bottom: 1px solid #292524;
          line-height: 1.55;
          vertical-align: top;
        }
        .post-content hr {
          border: none;
          border-top: 1px dashed #44403c;
          margin: 2.6em 0;
        }
        .post-content em {
          color: #e7e5e4;
          font-style: italic;
        }
        /* Pull-quote: any paragraph in the article that starts with "« or " makes a callout */
        .faq-answer p { color: #d6d3d1; line-height: 1.7; margin: 0.5em 0; }
        .faq-answer strong {
          color: #ff9655;
          background: linear-gradient(180deg, transparent 65%, rgba(224,101,31,0.22) 65%);
          padding: 0 2px;
        }
        .faq-answer ul, .faq-answer ol {
          margin: 0.7em 0;
          padding-left: 1.2em;
        }
        .faq-answer li { margin-bottom: 0.3em; }
        @media (max-width: 640px) {
          .post-content p { font-size: 16px; }
          .post-content h2 { font-size: 24px; margin-top: 2.2em; padding-left: 16px; }
          .post-content h3 { font-size: 19px; }
        }
      `}</style>
    </div>
  );
}
